import { useState, useEffect, useRef } from 'react';
import { X, Smartphone, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../api';
import { getGPS } from '../gps';

const SERVICE_ICONS = {
  sound_box: '🔊', upi_qr: '📱', current_savings: '🏦',
  daily_account: '📅', fixed_deposit: '📈', recurring_deposit: '🔁',
  monthly_interest: '💵', loan: '💰', other: '📦'
};

export function AddMerchantSheet({ isOpen, onClose, onSuccess, editMerchant = null }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    businessName: '', ownerName: '', phone: '', address: '', city: '',
    nextVisitDate: '', notes: '', interestedServices: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneStatus, setPhoneStatus] = useState({ checking: false, exists: false, checked: false });
  const [showUPIBanner, setShowUPIBanner] = useState(false);
  const phoneCheckTimeout = useRef(null);
  const isEdit = !!editMerchant;

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data)).catch(() => {});
  }, []);

  // Pre-populate form when editing
  useEffect(() => {
    if (editMerchant && isOpen) {
      const serviceIds = editMerchant.interestedServices ||
        (editMerchant.services || []).map(s => s.id) || [];
      setForm({
        businessName: editMerchant.businessName || '',
        ownerName: editMerchant.ownerName || '',
        phone: editMerchant.phone || '',
        address: editMerchant.address || '',
        city: editMerchant.city || '',
        nextVisitDate: editMerchant.nextVisitDate || '',
        notes: '',
        interestedServices: serviceIds
      });
      // Check if UPI is selected
      const hasUpi = (editMerchant.services || []).some(s => s.slug === 'upi_qr');
      setShowUPIBanner(hasUpi);
      setPhoneStatus({ checking: false, exists: false, checked: false });
      setError('');
    }
  }, [editMerchant, isOpen]);

  // UPI/QR is primary - Current/Savings gets auto-added
  const upiService = services.find(s => s.slug === 'upi_qr');
  const caService = services.find(s => s.slug === 'current_savings');

  // Real-time phone duplicate check
  const checkPhoneDuplicate = async (phone) => {
    if (phone.length !== 10) {
      setPhoneStatus({ checking: false, exists: false, checked: false });
      return;
    }
    // Skip check if phone hasn't changed in edit mode
    if (isEdit && phone === editMerchant.phone) {
      setPhoneStatus({ checking: false, exists: false, checked: true });
      return;
    }
    setPhoneStatus(p => ({ ...p, checking: true }));
    try {
      const res = await api.get(`/merchants/check-phone/${phone}`);
      setPhoneStatus({ checking: false, exists: res.data.exists, checked: true });
    } catch {
      setPhoneStatus({ checking: false, exists: false, checked: false });
    }
  };

  const handlePhoneChange = (value) => {
    const cleanPhone = value.replace(/\D/g, '').slice(0, 10);
    setForm(p => ({ ...p, phone: cleanPhone }));
    
    if (phoneCheckTimeout.current) clearTimeout(phoneCheckTimeout.current);
    if (cleanPhone.length === 10) {
      phoneCheckTimeout.current = setTimeout(() => checkPhoneDuplicate(cleanPhone), 500);
    } else {
      setPhoneStatus({ checking: false, exists: false, checked: false });
    }
  };

  const handleServiceToggle = (serviceId, slug) => {
    const isUPI = slug === 'upi_qr';
    let newSelected = [...form.interestedServices];

    if (newSelected.includes(serviceId)) {
      newSelected = newSelected.filter(id => id !== serviceId);
      if (isUPI) {
        if (caService) {
          newSelected = newSelected.filter(id => id !== caService.id);
        }
        setShowUPIBanner(false);
      }
    } else {
      newSelected.push(serviceId);
      if (isUPI) {
        if (caService && !newSelected.includes(caService.id)) {
          newSelected.push(caService.id);
        }
        setShowUPIBanner(true);
      }
    }
    setForm(p => ({ ...p, interestedServices: newSelected }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.businessName.trim()) { setError('Business name is required.'); return; }
    if (!form.ownerName.trim()) { setError('Owner name is required.'); return; }
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone.trim())) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (phoneStatus.exists) {
      setError('This phone number is already registered.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      if (isEdit) {
        // Edit mode - PATCH
        const payload = {
          businessName: form.businessName,
          ownerName: form.ownerName,
          phone: form.phone.trim(),
          address: form.address,
          city: form.city,
          nextVisitDate: form.nextVisitDate || null,
          interestedServices: form.interestedServices
        };
        await api.patch(`/merchants/${editMerchant.id}`, payload);
        onSuccess({ ...editMerchant, ...payload, id: editMerchant.id });
        handleClose();
      } else {
        // Add mode - POST
        const gps = getGPS();
        const payload = {
          ...form,
          phone: form.phone.trim(),
          gps_lat: gps?.lat || null,
          gps_lng: gps?.lng || null,
        };
        const res = await api.post('/merchants', payload);
        onSuccess(res.data);
        resetForm();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || (isEdit ? 'Failed to update merchant.' : 'Failed to register merchant.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ businessName: '', ownerName: '', phone: '', address: '', city: '', nextVisitDate: '', notes: '', interestedServices: [] });
    setError('');
    setPhoneStatus({ checking: false, exists: false, checked: false });
    setShowUPIBanner(false);
  };

  const handleClose = () => { if (!isEdit) resetForm(); onClose(); };

  // Check if UPI is selected (for auto-include visual)
  const isUPISelected = upiService && form.interestedServices.includes(upiService.id);

  return (
    <div className={`sh-ov ${isOpen ? 'open' : ''}`} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="sheet" role="dialog" aria-label={isEdit ? 'Edit Merchant' : 'Add New Merchant'}>
        <div className="sh-handle" />
        <div className="sh-head">
          <span className="sh-title" data-testid="sheet-title">{isEdit ? 'Edit Merchant' : 'Add New Merchant'}</span>
          <button className="sh-close" onClick={handleClose} data-testid="close-sheet-btn"><X size={14} /></button>
        </div>

        <div className="sh-body">
          {error && <div className="error-msg" data-testid="merchant-form-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="fg">
              <label className="fl">Business Name <span className="req">*</span></label>
              <input className="fi" type="text" placeholder="e.g. Ramesh General Store"
                value={form.businessName} onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))}
                required data-testid="business-name-input" />
            </div>
            <div className="fg">
              <label className="fl">Owner Name <span className="req">*</span></label>
              <input className="fi" type="text" placeholder="e.g. Ramesh Patil"
                value={form.ownerName} onChange={e => setForm(p => ({ ...p, ownerName: e.target.value }))}
                required data-testid="owner-name-input" />
            </div>
            <div className="fg">
              <label className="fl">Phone Number <span className="req">*</span></label>
              <input 
                className={`fi ${phoneStatus.exists ? 'error' : ''}`}
                type="tel" 
                placeholder="10-digit mobile number"
                value={form.phone} 
                onChange={e => handlePhoneChange(e.target.value)}
                inputMode="numeric" 
                maxLength={10} 
                required 
                data-testid="phone-input" 
              />
              {phoneStatus.checking && (
                <div className="phone-status checking">Checking...</div>
              )}
              {phoneStatus.exists && (
                <div className="phone-status error" data-testid="phone-duplicate-error">
                  <AlertCircle size={12} /> This number is already registered
                </div>
              )}
              {phoneStatus.checked && !phoneStatus.exists && form.phone.length === 10 && (
                <div className="phone-status valid">
                  <CheckCircle size={12} /> Number available
                </div>
              )}
              <div className="fhint">10-digit number (without +91)</div>
            </div>
            <div className="fg">
              <label className="fl">{isEdit ? 'Reschedule Next Visit' : 'Next Follow-up Date'}</label>
              <input className="fi" type="date" value={form.nextVisitDate}
                onChange={e => setForm(p => ({ ...p, nextVisitDate: e.target.value }))}
                data-testid="followup-date-input" />
            </div>
            <div className="frow fg">
              <div>
                <label className="fl">Street / Area</label>
                <input className="fi" type="text" placeholder="Street or area" value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div>
                <label className="fl">City</label>
                <input className="fi" type="text" placeholder="City" value={form.city}
                  onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
              </div>
            </div>

            <div className="fg">
              <label className="fl">Interested Services</label>
              {/* UPI/QR Auto-Include Banner */}
              {showUPIBanner && (
                <div className="sb-ban show">
                  <Smartphone size={16} />
                  <span>UPI/QR selected — <strong>Current/Savings Account</strong> auto-included</span>
                </div>
              )}
              {/* 2x2 Checkbox Grid with Icons */}
              <div className="cbg">
                {services.map(s => {
                  const isSelected = form.interestedServices.includes(s.id);
                  const isPrimary = s.slug === 'upi_qr';
                  const isAutoAdded = isUPISelected && s.slug === 'current_savings';
                  const icon = SERVICE_ICONS[s.slug] || '📋';
                  return (
                    <label 
                      key={s.id} 
                      className={`cbi ${isSelected ? 'on' : ''} ${isPrimary ? 'sb' : ''}`}
                      data-testid={`service-${s.slug}`}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => !isAutoAdded && handleServiceToggle(s.id, s.slug)}
                        disabled={isAutoAdded}
                      />
                      <span className="cbi-icon">{icon}</span>
                      <span className="cbl">
                        {s.name}
                        {isPrimary && <span style={{ color: 'var(--red)', fontSize: '9px', marginLeft: '4px' }}>★</span>}
                        {isAutoAdded && <span style={{ color: 'var(--g3)', fontSize: '9px', marginLeft: '4px' }}>(auto)</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {!isEdit && (
              <div className="fg">
                <label className="fl">Initial Notes (optional)</label>
                <textarea className="fi" rows={2} placeholder="Any notes about this merchant..."
                  value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  data-testid="notes-input" />
              </div>
            )}

            <button 
              type="submit" 
              className="sub-btn" 
              disabled={loading || phoneStatus.exists} 
              data-testid="save-merchant-btn"
            >
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Merchant'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
